import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { customerApi, CUSTOMER_TOKEN_KEY } from "@/lib/api";
import { authMessage, shopAuth } from "@/lib/supabase";

/*
  Customer accounts on Supabase Auth (email/password, Google, email confirmation, password reset).
  The current access token is mirrored to localStorage[CUSTOMER_TOKEN_KEY], which customerApi() sends
  to the backend; supabase-js refreshes it automatically.
*/
const CustomerAuthContext = createContext(null);

const mirror = (session) => {
  if (session?.access_token) localStorage.setItem(CUSTOMER_TOKEN_KEY, session.access_token);
  else localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  return session?.access_token || "";
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null); // null = checking, false = guest, object = authed
  const [token, setToken] = useState(localStorage.getItem(CUSTOMER_TOKEN_KEY) || "");

  const loadProfile = useCallback(async (session) => {
    setToken(mirror(session));
    if (!session) { setCustomer(false); return false; }
    try {
      const { data } = await customerApi().get("/account/me");
      setCustomer(data);
      return data;
    } catch {
      setCustomer(false); // e.g. a staff login, which has no customer profile
      return false;
    }
  }, []);

  useEffect(() => {
    shopAuth.auth.getSession().then(({ data }) => loadProfile(data.session));
    const { data } = shopAuth.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED") { setToken(mirror(session)); return; }
      setTimeout(() => loadProfile(session), 0); // don't call Supabase inside this callback
    });
    return () => data.subscription.unsubscribe();
  }, [loadProfile]);

  const signedIn = async (session) => {
    const profile = await loadProfile(session);
    if (!profile) throw new Error("We couldn't open your account. Please try again.");
    return profile;
  };

  const login = async (email, password, turnstileToken) => {
    const { data, error } = await shopAuth.auth.signInWithPassword({
      email, password, options: { captchaToken: turnstileToken || undefined } });
    if (error) throw new Error(authMessage(error));
    return signedIn(data.session);
  };

  // Returns the customer when signed straight in, or { confirmEmail: true } when Supabase has
  // emailed a confirmation link first.
  const register = async (name, email, password, turnstileToken) => {
    const { data, error } = await shopAuth.auth.signUp({
      email, password,
      options: { data: { full_name: name }, emailRedirectTo: `${window.location.origin}/verify-email`, captchaToken: turnstileToken || undefined },
    });
    if (error) throw new Error(authMessage(error));
    if (!data.session) {
      // Supabase returns a user with no identities when the email is already registered.
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        throw new Error("An account with this email already exists. Try signing in.");
      }
      return { confirmEmail: true };
    }
    return signedIn(data.session);
  };

  const googleLogin = async (credential) => {
    const { data, error } = await shopAuth.auth.signInWithIdToken({ provider: "google", token: credential });
    if (error) throw new Error(authMessage(error));
    return signedIn(data.session);
  };

  const requestPasswordReset = async (email, turnstileToken) => {
    const { error } = await shopAuth.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`, captchaToken: turnstileToken || undefined });
    if (error) throw new Error(authMessage(error));
  };

  // From the reset link (recovery session) - choose a new password.
  const setNewPassword = async (password) => {
    const { error } = await shopAuth.auth.updateUser({ password });
    if (error) throw new Error(authMessage(error));
  };

  // From account settings: re-check the current password first.
  const changePassword = async (currentPassword, newPassword, turnstileToken) => {
    const email = customer?.email;
    const { error: e1 } = await shopAuth.auth.signInWithPassword({
      email, password: currentPassword, options: { captchaToken: turnstileToken || undefined } });
    if (e1) throw new Error("Your current password is incorrect.");
    const { error } = await shopAuth.auth.updateUser({ password: newPassword });
    if (error) throw new Error(authMessage(error));
  };

  const resendConfirmation = async (email, turnstileToken) => {
    const { error } = await shopAuth.auth.resend({ type: "signup", email,
      options: { emailRedirectTo: `${window.location.origin}/verify-email`, captchaToken: turnstileToken || undefined } });
    if (error) throw new Error(authMessage(error));
  };

  const refreshCustomer = useCallback(async () => {
    const { data } = await customerApi().get("/account/me");
    setCustomer(data);
    return data;
  }, []);

  const logout = async () => {
    await shopAuth.auth.signOut();
    mirror(null);
    setToken("");
    setCustomer(false);
  };

  return (
    <CustomerAuthContext.Provider
      value={{ customer, token, login, register, googleLogin, logout, refreshCustomer,
               requestPasswordReset, setNewPassword, changePassword, resendConfirmation }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);
